import React, {useState} from 'react';
import {t} from 'i18next';
import {useFetchNeedRatingProblemsQuery} from '@goproblems/shared/api/indexPage';
import ProblemPictureSkeleton from '@components/skeleton/ProblemPictureSkeleton';
import useNumberOfItemsPerRow from '@utils/hooks/useNumberOfItemsPerRow';
import IndexSectionHeading from './IndexSectionHeading';
import {ProblemMinimal} from '@types';
import Rank from '@components/common/RankText';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';
import {ThemeAwareBadge} from '@components/theme/ThemeAwareBadge';
import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';

const NeedRatingProblemCard: React.FC<{
    offset: number;
    problem: ProblemMinimal;
}> = ({problem, offset}) => {
    const {t} = useTranslation();
    const [imageLoaded, setImageLoaded] = useState(false);

    return (
        <div className="card relative">
            <span className="absolute inset-x-0 top-0 z-10">
                <ThemeAwareBadge className="m-1">
                    <Rank rank={problem.rank} />
                </ThemeAwareBadge>
            </span>
            <Link
                to={`/problems/${problem.id}?name=needingRating&offset=${offset}`}
                className="text-decoration-none">
                {!imageLoaded && (
                    <ThemeAwareSkeleton
                        style={{
                            width: '100%',
                            aspectRatio: 1.02, // compensate for the size gap between the skeleton and the image
                        }}
                    />
                )}
                <img
                    onLoad={() => setImageLoaded(true)}
                    className="block h-auto w-full"
                    style={{display: imageLoaded ? 'block' : 'none'}}
                    src={problem.imageUrl}
                    alt={t('common.board_thumbnail')}
                />
            </Link>
        </div>
    );
};

const NeedRatingProblem = () => {
    const {
        data: needRatingProblemCollection,
        isLoading,
        isError,
    } = useFetchNeedRatingProblemsQuery();
    const numberOfItems = useNumberOfItemsPerRow();

    // Determine the number of placeholders needed
    const placeholders = needRatingProblemCollection
        ? Math.max(0, numberOfItems - needRatingProblemCollection.length)
        : 0;

    // Utility function to generate visibility classes based on index
    const getVisibilityClasses = (index: number): string => {
        if (index >= 8) return 'hidden site-xxl:block';
        if (index >= 6) return 'hidden site-xl:block';
        if (index >= 4) return 'hidden md:block';
        if (index >= 2) return 'hidden site-sm:block';
        return '';
    };

    if (isError) {
        return (
            <div>{t('index.section_load_error')}</div>
        );
    }

    if (needRatingProblemCollection?.length === 0) {
        return null;
    }

    return (
        <div className="index-home-feature index-home-card-list w-full">
            <IndexSectionHeading
                title={t('index.problems_needing_rating')}
                to="/problems/need-rating"
            />
            <div className="flex flex-nowrap gap-2">
                {isLoading
                    ? Array.from(
                          {length: numberOfItems},
                          (_, index) => (
                              <div
                                  key={`skeleton-${index}`}
                                  className={`min-w-0 flex-1 ${getVisibilityClasses(index)}`}>
                                  <ProblemPictureSkeleton />
                              </div>
                          )
                      )
                    : needRatingProblemCollection
                          ?.slice(0, numberOfItems)
                          .map((problem, index) => (
                              <div
                                  key={problem.id}
                                  className={`min-w-0 flex-1 ${getVisibilityClasses(index)}`}>
                                  <NeedRatingProblemCard
                                      problem={problem}
                                      offset={index}
                                  />
                              </div>
                          ))}
                {/* Render placeholders if necessary */}
                {!isLoading &&
                    Array.from({length: placeholders}, (_, index) => (
                        <div
                            key={`placeholder-${index}`}
                            className={`min-w-0 flex-1 ${getVisibilityClasses(index)}`}
                        />
                    ))}
            </div>
        </div>
    );
};

export default NeedRatingProblem;
